import React, { useEffect } from 'react';
import { toast } from 'react-toastify';

interface MyEvent {
    id: string;
    title: string;
    start: Date;
    end: Date;
    description?: string;
    reminded?: boolean;
}

const GlobalReminderChecker: React.FC = () => {
    useEffect(() => {
        const openDB = (): Promise<IDBDatabase> => {
            return new Promise((resolve, reject) => {
                const request = indexedDB.open('CalendarDB', 1);
                
                request.onerror = () => reject('Error opening database');
                request.onsuccess = () => resolve(request.result);
            });
        };

        const getEventsFromDB = async (db: IDBDatabase): Promise<MyEvent[]> => {
            return new Promise((resolve, reject) => {
                const transaction = db.transaction('events', 'readonly');
                const store = transaction.objectStore('events');
                const request = store.getAll();
                
                request.onerror = () => reject('Error getting events');
                request.onsuccess = () => resolve(request.result);
            });
        };

        const checkForReminders = async () => {
            if (!('indexedDB' in window)) return;

            try {
                const db = await openDB();
                const events = await getEventsFromDB(db);
                const now = new Date();
                const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);

                events.forEach((event: MyEvent) => {
                    const eventTime = new Date(event.start);
                    if (
                        !event.reminded && 
                        eventTime > thirtyMinutesAgo && 
                        eventTime <= now
                    ) {
                        // Show reminder
                        toast.info(
                            <div>
                                <strong>Reminder: {event.title}</strong>
                                <div>Starting now!</div>
                            </div>,
                            { autoClose: 10000 }
                        );

                        // Mark as reminded
                        const updateTransaction = db.transaction('events', 'readwrite');
                        const updateStore = updateTransaction.objectStore('events');
                        updateStore.put({ ...event, reminded: true });
                    }
                });
            } catch (e) {
                console.error('Error checking reminders', e);
            }
        };

        // Check every minute
        const interval = setInterval(checkForReminders, 60000);
        checkForReminders(); // Initial check

        return () => clearInterval(interval);
    }, []);

    return null;
};

export default GlobalReminderChecker;